import React, { useContext, useEffect, useMemo, useState } from 'react'
import appStyle from '../../style.less'
import style from './style.less'
import Typography, {
  TypographySizes,
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Button from '@yaak/components/src/Button'
import Switch from '@yaak/components/src/Switch'
import {
  getSimModuleCatalog,
  updateSimModuleActivation,
  updateSimModuleCustom,
  SimModuleCatalogEntry,
} from '@yaak/components/services/api/api'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import {
  formatGearModes,
  getActivationDiff,
  getCustomDiff,
  groupModules,
  hasActiveChanges,
  hasCustomChanges,
} from '../../helpers/moduleConfig'

interface SimModulesPageProps {
  token: string
}

const SimModulesPage: React.FunctionComponent<SimModulesPageProps> = ({
  token,
}) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [originalModules, setOriginalModules] = useState<
    SimModuleCatalogEntry[]
  >([])
  const [localModules, setLocalModules] = useState<SimModuleCatalogEntry[]>([])

  const { setShowToast } = useContext(ToastContext) as ToastContextType

  useEffect(() => {
    const fetchCatalog = async () => {
      const catalog = await getSimModuleCatalog({
        token,
        onAlert: setShowToast,
      })
      const modules = (catalog?.modules || []).map((module) => ({
        ...module,
        isCustom: Boolean(module.isCustom),
      }))
      setOriginalModules(modules)
      setLocalModules(modules)
      setLoading(false)
    }

    token && fetchCatalog()
  }, [token])

  const toggleModule = (moduleId: string) => {
    setLocalModules((prev) =>
      prev.map((m) =>
        m.moduleId === moduleId ? { ...m, active: !m.active } : m
      )
    )
  }

  const toggleCustom = (moduleId: string) => {
    setLocalModules((prev) =>
      prev.map((m) =>
        m.moduleId === moduleId ? { ...m, isCustom: !m.isCustom } : m
      )
    )
  }

  const hasChanges =
    hasActiveChanges(localModules, originalModules) ||
    hasCustomChanges(localModules, originalModules)

  const save = async () => {
    const { toActivate, toDeactivate } = getActivationDiff(
      localModules,
      originalModules
    )
    const { toMarkCustom, toUnmarkCustom } = getCustomDiff(
      localModules,
      originalModules
    )
    if (
      toActivate.length === 0 &&
      toDeactivate.length === 0 &&
      toMarkCustom.length === 0 &&
      toUnmarkCustom.length === 0
    ) {
      return
    }

    setSaving(true)
    let catalog

    if (toDeactivate.length > 0) {
      catalog = await updateSimModuleActivation({
        token,
        moduleIds: toDeactivate,
        active: false,
        onAlert: setShowToast,
      })
      if (!catalog?.modules) {
        setSaving(false)
        return
      }
    }

    if (toActivate.length > 0) {
      const activated = await updateSimModuleActivation({
        token,
        moduleIds: toActivate,
        active: true,
        onAlert: setShowToast,
      })
      if (!activated?.modules) {
        if (catalog?.modules) {
          setOriginalModules(catalog.modules)
          setLocalModules(catalog.modules)
        }
        setSaving(false)
        return
      }
      catalog = activated
    }

    if (toUnmarkCustom.length > 0) {
      const unmarked = await updateSimModuleCustom({
        token,
        moduleIds: toUnmarkCustom,
        custom: false,
        onAlert: setShowToast,
      })
      if (!unmarked?.modules) {
        if (catalog?.modules) {
          setOriginalModules(catalog.modules)
          setLocalModules(catalog.modules)
        }
        setSaving(false)
        return
      }
      catalog = unmarked
    }

    if (toMarkCustom.length > 0) {
      const marked = await updateSimModuleCustom({
        token,
        moduleIds: toMarkCustom,
        custom: true,
        onAlert: setShowToast,
      })
      if (marked?.modules) {
        catalog = marked
      }
    }

    if (catalog?.modules) {
      setOriginalModules(catalog.modules)
      setLocalModules(catalog.modules)
    }

    setSaving(false)
  }

  const grouped = useMemo(() => groupModules(localModules), [localModules])

  return (
    <div className={appStyle.page}>
      <div className={style.container}>
        <div className={style.header}>
          <Typography
            type={TypographyTypes.headline}
            size={TypographySizes.small}
          >
            {'Sim Modules'}
          </Typography>
          <Button
            onClick={save}
            text={saving ? 'Saving...' : 'Save'}
            disabled={!hasChanges || saving}
          />
        </div>
        <Divider />
      </div>
      {loading ? (
        <ProgressBar />
      ) : localModules.length === 0 ? (
        <div className={style.emptyState}>
          <Typography type={TypographyTypes.body}>
            {'No modules found.'}
          </Typography>
        </div>
      ) : (
        <div className={style.modulesContainer}>
          <table className={style.moduleTable}>
            <thead>
              <tr>
                <th className={style.thIndex}>#</th>
                <th className={style.thName}>Module</th>
                <th className={style.thGear}>Gear modes</th>
                <th className={style.thVersions}>Supported versions</th>
                <th className={style.thActive}>Active</th>
                <th className={style.thActive}>Custom</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => (
                <React.Fragment key={group.groupName}>
                  <tr className={style.groupRow}>
                    <td colSpan={6} className={style.groupCell}>
                      <Typography type={TypographyTypes.title}>
                        {group.groupName}
                      </Typography>
                    </td>
                  </tr>
                  {group.modules.map((module) => (
                    <tr key={module.moduleId}>
                      <td className={style.tdIndex}>{module.displayIndex}</td>
                      <td className={style.tdName}>{module.title}</td>
                      <td className={style.tdGear}>
                        {formatGearModes(module.gearModes)}
                      </td>
                      <td className={style.tdVersions}>
                        {module.supportedVersions?.length
                          ? module.supportedVersions.join(', ')
                          : '-'}
                      </td>
                      <td className={style.tdActive}>
                        <Switch
                          checked={module.active}
                          onChange={() => toggleModule(module.moduleId)}
                        />
                      </td>
                      <td className={style.tdActive}>
                        <Switch
                          checked={module.isCustom}
                          onChange={() => toggleCustom(module.moduleId)}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default SimModulesPage
